import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, MapPin, Clock, ChevronRight, CheckCircle2, AlertCircle, RotateCcw, ChevronDown, FileWarning, History as HistoryIcon, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { orgToday } from "@/lib/tz";

const fmtTime = (dt) => {
  if (!dt) return "";
  const t = String(dt).split(" ")[1] || "";
  return t.slice(0, 5);
};
const dayLabel = (ds) => {
  const d = new Date(ds + "T00:00:00");
  return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" });
};
const todayStr = () => orgToday();

const STATUS_STYLE = {
  "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Completed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Assigned": "bg-emerald-500/15 text-[#00c86e] border-emerald-500/30",
  "Published": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Draft": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

// Derive display label for worker roster — workers always see their own shifts
// so an assigned shift (support_worker present) in Draft/Published shows "Assigned".
const workerBadge = (s) => {
  if (s.actual_clock_out) return "Clocked out";
  if (s.actual_clock_in) return "On shift";
  if (s.status === "In Progress") return "In Progress";
  if (s.status === "Completed") return "Completed";
  // On the worker's own roster they're always assigned — show that.
  if (s.support_worker) return "Assigned";
  return s.status;
};

export default function WorkerRoster() {
  const nav = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returned, setReturned] = useState({ count: 0, items: [] });
  const [showReturned, setShowReturned] = useState(false);
  const [toSubmit, setToSubmit] = useState({ count: 0, items: [] });
  const [showToSubmit, setShowToSubmit] = useState(true);
  const [historical, setHistorical] = useState(false);
  const todayRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.workerShifts({ historical })
      .then(setShifts)
      .catch(() => toast.error("Could not load your roster."))
      .finally(() => setLoading(false));
  }, [historical]);

  useEffect(() => {
    api.workerReturned().then(setReturned).catch(() => {});
    api.workerToSubmit().then(setToSubmit).catch(() => {});
  }, []);

  const today = todayStr();
  const groups = shifts.reduce((acc, s) => {
    (acc[s.shift_date] = acc[s.shift_date] || []).push(s);
    return acc;
  }, {});
  const days = Object.keys(groups).sort(); // ascending: history at top, upcoming below

  // Land on Today so scrolling up shows history and scrolling down shows upcoming.
  useEffect(() => {
    if (!loading && todayRef.current) {
      const t = setTimeout(() => todayRef.current?.scrollIntoView({ block: "start" }), 150);
      return () => clearTimeout(t);
    }
  }, [loading, shifts.length]);

  if (loading)
    return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>;

  return (
    <div className="px-4 pt-5 pb-10" data-testid="worker-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight font-brand">{historical ? "Historical Shifts" : "My Roster"}</h1>
          <p className="text-xs text-slate-400 mt-1 mb-4">{historical ? "Shifts you've completed and had approved" : "Today's the top — scroll up for history, down for what's coming"}</p>
        </div>
        <button onClick={() => setHistorical((v) => !v)} data-testid="worker-historical-toggle"
          className="shrink-0 h-9 px-3 rounded-full border border-white/15 text-[11px] font-semibold text-slate-200 hover:bg-white/10 flex items-center gap-1.5">
          <HistoryIcon className="h-3.5 w-3.5" />{historical ? "Back to roster" : "Historical"}
        </button>
      </div>

      {returned.count > 0 && (
        <div className="mb-5" data-testid="worker-returned-alert">
          <button onClick={() => setShowReturned((v) => !v)} data-testid="worker-returned-alert-toggle"
            className="w-full flex items-center justify-between rounded-2xl border border-red-500/50 bg-red-500/15 px-4 py-3 active:scale-[0.99] transition-transform">
            <div className="flex items-center gap-2 text-red-200 font-semibold text-sm">
              <RotateCcw className="h-4 w-4" />
              {returned.count} returned shift{returned.count > 1 ? "s" : ""} — action needed
            </div>
            <ChevronDown className={`h-4 w-4 text-red-200 transition-transform ${showReturned ? "rotate-180" : ""}`} />
          </button>
          {showReturned && (
            <div className="mt-2 space-y-2" data-testid="worker-returned-list">
              {returned.items.map((it) => (
                <button key={it.shift} onClick={() => nav(`/worker/shift/${it.shift}`)}
                  data-testid={`worker-returned-item-${it.shift}`}
                  className="w-full text-left rounded-xl border border-red-500/25 bg-red-500/5 hover:bg-red-500/10 p-3 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="text-white text-sm font-semibold">{it.shift_date ? dayLabel(it.shift_date) : it.shift}</div>
                    <ChevronRight className="h-4 w-4 text-red-300" />
                  </div>
                  {it.participant_name && <div className="text-[11px] text-slate-300 mt-0.5">{it.participant_name} <span className="text-[10px] text-slate-500 font-mono">· {it.shift}</span></div>}
                  <ul className="mt-1.5 space-y-1">
                    {it.reasons.map((r, i) => (
                      <li key={i} className="text-[11px] text-red-200"><span className="font-semibold">{r.type}:</span> {r.reason || "please review"}</li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {toSubmit.count > 0 && (
        <div className="mb-5" data-testid="worker-tosubmit-alert">
          <button onClick={() => setShowToSubmit((v) => !v)} data-testid="worker-tosubmit-toggle"
            className="w-full flex items-center justify-between rounded-2xl border border-amber-500/50 bg-amber-500/15 px-4 py-3 active:scale-[0.99] transition-transform">
            <div className="flex items-center gap-2 text-amber-200 font-semibold text-sm">
              <FileWarning className="h-4 w-4" />
              {toSubmit.count} timesheet{toSubmit.count > 1 ? "s" : ""} to submit
            </div>
            <ChevronDown className={`h-4 w-4 text-amber-200 transition-transform ${showToSubmit ? "rotate-180" : ""}`} />
          </button>
          {showToSubmit && (
            <div className="mt-2 space-y-2" data-testid="worker-tosubmit-list">
              {toSubmit.items.map((it) => {
                const h = it.hours_since_clock_out;
                const ago = h == null ? "" : h < 1 ? `${Math.round(h * 60)} min ago` : h < 48 ? `${Math.round(h)}h ago` : `${Math.round(h / 24)} days ago`;
                return (
                  <button key={it.name} onClick={() => nav(`/worker/shift/${it.name}`)}
                    data-testid={`worker-tosubmit-item-${it.name}`}
                    className="w-full text-left rounded-xl border border-amber-500/25 bg-amber-500/5 hover:bg-amber-500/10 p-3 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-semibold">{it.shift_date ? dayLabel(it.shift_date) : it.name}</div>
                      <ChevronRight className="h-4 w-4 text-amber-300" />
                    </div>
                    {it.participant_name && <div className="text-[11px] text-slate-300 mt-0.5">{it.participant_name} <span className="text-[10px] text-slate-500 font-mono">· {it.name}</span></div>}
                    <div className="text-[11px] text-amber-200 mt-0.5">Clocked out {ago} — notes &amp; timesheet not submitted</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {days.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400 text-sm" data-testid="worker-no-shifts">
          No shifts on your roster.
        </div>
      )}

      {days.map((ds) => {
        const isToday = ds === today;
        const isPast = ds < today;
        return (
          <div key={ds} className="mb-6" ref={isToday ? todayRef : null} data-testid={`worker-roster-day-${ds}`}>
            <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 flex items-center gap-2 ${isToday ? "text-[#00c86e]" : isPast ? "text-slate-500" : "text-emerald-300/80"}`}>
              {isToday && <span className="text-[9px] bg-[#00c86e] text-white px-1.5 py-0.5 rounded-full normal-case tracking-normal">Today</span>}
              {dayLabel(ds)}
            </div>
            <div className={`space-y-2.5 ${isPast ? "opacity-70" : ""}`}>
              {groups[ds].map((s) => (
                <button key={s.name} onClick={() => nav(`/worker/shift/${s.name}`)}
                  data-testid={`worker-shift-${s.name}`}
                  className="w-full text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 active:scale-[0.99] transition-all p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white font-semibold text-[15px]">
                      <Clock className="h-4 w-4 text-[#00c86e]" />
                      {fmtTime(s.start_time)} – {fmtTime(s.end_time)}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[workerBadge(s)] || STATUS_STYLE.Published}`}>
                      {workerBadge(s)}
                    </span>
                  </div>
                  <div className="mt-2 text-white/90 text-sm font-medium flex items-center gap-1.5" data-testid={s.masked ? `worker-shift-masked-${s.name}` : undefined}>
                    {s.masked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
                    <span className={s.masked ? "text-slate-400 italic" : ""}>{s.participant_name}</span>
                  </div>
                  {s.worker_count > 1 && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400" data-testid={`worker-shift-multi-${s.name}`}>
                      <Users className="h-3 w-3" /> {s.worker_count} workers
                    </div>
                  )}
                  {s.location && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                      <MapPin className="h-3 w-3" /> {s.location}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-[11px] text-slate-400">
                      {s.completion_notes ? (
                        <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Notes done</span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-500"><AlertCircle className="h-3 w-3" /> Notes pending</span>
                      )}
                      <span>{s.rostered_hours}h</span>
                      <span className="text-[10px] text-slate-600 font-mono" data-testid={`worker-shift-id-${s.name}`}>{s.name}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
